import { createContext, useContext, useReducer } from 'react';
import { useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { OfficialDiaryPublicationState, OfficialDiaryPublicationReducer, ACTION_TYPES } from './official-diary-publication-reducer';
import { IPreviewPublicationResponse } from '../../../../model/sdp/official-daily/response/preview-publication-response';
import ApplicationAppService from '../../../../application/platform/application-app-service';
import { toApiDate } from '../../../../utils/date';
import { parseNumberInput } from '../../../../utils/format';

const OfficialDiaryPublicationContext = createContext<OfficialDiaryPublicationState | undefined>(undefined);
const OfficialDiaryPublicationDispatchContext = createContext({} as any);

export const getTodayApiDate = () => toApiDate(dayjs()) ?? undefined;

const OfficialDiaryPublicationProvider = ({ children }: { children: React.ReactNode }) => {
    const authenticationData = ApplicationAppService.getAuthenticationData();
    const { submissionPublicationId } = useParams();

    const initialState: OfficialDiaryPublicationState = {
        activeStep: 0,
        files: [],
        previewPublicationRequest: {
            republication: false,
            submissionPublicationId: parseNumberInput(submissionPublicationId),
            clientId: authenticationData?.type === 1 ? authenticationData.company.id : undefined,
            publicationDateOn: getTodayApiDate()
        },
        previewPublicationResponse: {} as IPreviewPublicationResponse,
        lookups: { categories: [], subcategories: [], publicAgencies: [], documentTypes: [] },
        showValidationErrors: false
    };

    const [state, dispatch] = useReducer(OfficialDiaryPublicationReducer, initialState);

    return (
        <OfficialDiaryPublicationContext.Provider value={state}>
            <OfficialDiaryPublicationDispatchContext.Provider value={dispatch}>
                {children}
            </OfficialDiaryPublicationDispatchContext.Provider>
        </OfficialDiaryPublicationContext.Provider>
    );
}

const useOfficialDiaryPublicationState = () => {
    const context = useContext(OfficialDiaryPublicationContext);
    if (context === undefined) {
        throw new Error('useOfficialDiaryPublicationState must be used within a OfficialDiaryPublicationProvider');
    }
    return context;
}

const useOfficialDiaryPublicationDispatch = () => {
    const context = useContext(OfficialDiaryPublicationDispatchContext);
    if (context === undefined) {
        throw new Error('useOfficialDiaryPublicationDispatch must be used within a OfficialDiaryPublicationProvider');
    }
    return context;
}

export { OfficialDiaryPublicationProvider, useOfficialDiaryPublicationDispatch, useOfficialDiaryPublicationState, ACTION_TYPES };
